import ExcelJs from 'exceljs'
import { cloneDeep, get, groupBy, has, uniq } from 'lodash'
import { Mark, Subject, Student, Slot } from '@/plugins/api'

const factors = [
  { multiply: 1, title: 'ĐĐGtx', quantity: 8 },
  { multiply: 2, title: 'ĐĐGgk', quantity: 2 },
  { multiply: 3, title: 'ĐĐGck', quantity: 1 }
]

function getEvaluateValue(val = '') {
  switch (val) {
    case 1:
      return 'Đ'
    case 2:
      return 'KĐ'
    default:
      return ''
  }
}

function getContentDataRow({ factors = [], maxCounts = 0, index = 0, marks = [], subject = {} }) {
  const row = [String(index + 1), get(subject, 'subjectGroup.title', '')]

  factors.forEach((factor, factorIndex) => {
    const factorMarks = marks.filter(mark => mark.factor.multiply === factor.multiply)

    if (factorMarks.length === 0) {
      for (let i = 0; i < maxCounts[factorIndex]; i++) {
        row.push('')
      }
    } else {
      factorMarks.forEach(mark => {
        let markValue = ''
        if (subject.markType === 'mark') markValue = mark.value || ''
        else markValue = getEvaluateValue(mark.value)
        row.push(markValue)
      })

      for (let i = factorMarks.length; i < maxCounts[factorIndex]; i++) {
        row.push('')
      }
    }
  })

  return row
}

function setHeader(worksheet) {
  worksheet.getRow(5).eachCell(cell => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.font = { bold: true }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }

    let selectedHeaders = ['STT', 'Môn học']
    if (selectedHeaders.includes(cell.value)) {
      cell.font = { bold: true, color: { argb: 'FFFF0000' } }
    } else {
      cell.font = { bold: true }
    }
  })
}

function settingCells(worksheet) {
  worksheet.getColumn(1).eachCell((cell, rowNumber) => {
    if (rowNumber >= 2) {
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
    }
  })
  worksheet.getRow(5).height = 60

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 4) {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      })
    }
  })
}

function setTitle({ ws = {}, semester = {}, student = {}, currentSchoolYear = {} }) {
  const description11 = 'Sở Giáo Dục và Đào Tạo Hà Nội'
  const description12 = 'Trường: THPT Chuyên Đại Học Sư Phạm'

  const description21 = `BẢNG ĐIỂM HỌC SINH ${student.name.toUpperCase()} (${student.code}) THEO MÔN HỌC`
  const description22 = `${semester.title} ${currentSchoolYear.name}`.toUpperCase()

  ws.addRow([description11, '', '', description21, '', ''])
  ws.mergeCells(1, 1, 1, 3)
  ws.mergeCells(1, 4, 1, 7)

  ws.addRow([description12, '', '', description22, '', ''])
  ws.mergeCells(2, 1, 2, 3)
  ws.mergeCells(2, 4, 2, 7)

  ws.addRow('')

  ws.getCell('A1').alignment = { horizontal: 'left' }
  ws.getCell('D1').alignment = { horizontal: 'center' }

  ws.getCell('A2').alignment = { horizontal: 'left' }
  ws.getCell('D2').alignment = { horizontal: 'center' }

  ws.getCell('A3').alignment = { horizontal: 'left' }
  ws.getCell('D3').alignment = { horizontal: 'center' }
  ws.addRow([])
}

async function onDownloadFile({ wb = {}, classData = {}, semester = {} }) {
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'test.xlsx'
  link.download = `Bảng điểm từng học sinh lớp ${classData.title} HK ${semester.title}.xlsx`
  link.click()
}

const getGroupMarksBySubject = ({ marks = [], subjects = [] }) => {
  const groupMarkBySubject = groupBy(marks, x => x.subject)
  const groupSubjectBySubjectId = groupBy(subjects, x => x.id)
  const tmp = {}

  Object.keys(groupSubjectBySubjectId).forEach(subjectId => {
    if (has(groupMarkBySubject, subjectId)) {
      tmp[subjectId] = groupMarkBySubject[subjectId]
    } else {
      tmp[subjectId] = []
    }
  })

  return cloneDeep(tmp)
}

const getMarkHeaders = ({ factors = [], maxCounts = [] }) => {
  const result = []
  factors.forEach((factor, index) => {
    result.push(factor.title)
    for (let i = 0; i < maxCounts[index] - 1; i++) {
      result.push('')
    }
  })
  return result
}

const getMaxCounts = ({ factors = [], groupMarkBySubject = {} }) => {
  const maxCounts = factors.map(x => x.quantity)
  Object.values(groupMarkBySubject).forEach(marks => {
    factors.forEach((factor, factorIndex) => {
      const count = marks.filter(m => m.factor.multiply === factor.multiply).length
      maxCounts[factorIndex] = Math.max(maxCounts[factorIndex], count, 1)
    })
  })
  return maxCounts
}

const getContentData = ({ groupMarkBySubject = {}, subjects = [], maxCounts = [] }) => {
  const res = []
  Object.keys(groupMarkBySubject).forEach((subjectId, index) => {
    const marks = groupMarkBySubject[subjectId]
    const subject = subjects.find(x => x.id === subjectId)
    res.push(getContentDataRow({ marks, factors, maxCounts, index, subject }))
  })
  return res
}

const mergeTableHeader = ({ ws = {}, maxCounts = [], header = [] }) => {
  let colIndex = header.length
  factors.forEach((_, index) => {
    if (maxCounts[index] > 1) {
      ws.mergeCells(5, colIndex + 1, 5, colIndex + maxCounts[index])
    }
    colIndex += maxCounts[index]
  })
}

const setColumnWidth = ({ ws = {}, marksHeader = [] }) => {
  ws.columns = [{ width: 10 }, { width: 30 }, ...marksHeader.map(() => ({ width: 10 }))]
}

const generateFile = async ({
  semester = {},
  students = [],
  currentSchoolYear = {},
  rawMarks = [],
  subjects = [],
  classData = {},
  onProgress = () => {}
}) => {
  const wb = new ExcelJs.Workbook()

  let progressIndex = 0

  for (const student of students) {
    const ws = wb.addWorksheet(`${student.name} (${student.code})`)

    const filteredMarks = rawMarks.filter(m => m.student === student.id)

    setTitle({ ws, semester, student, currentSchoolYear })

    const header = ['STT', 'Môn học']

    const groupMarkBySubject = getGroupMarksBySubject({ marks: filteredMarks, subjects })
    const maxCounts = getMaxCounts({ factors, groupMarkBySubject })
    const marksHeader = getMarkHeaders({ factors, maxCounts })
    const contentData = getContentData({ groupMarkBySubject, subjects, maxCounts })

    ws.addRow(header.concat(marksHeader))
    contentData.forEach(row => ws.addRow(row))

    mergeTableHeader({ ws, header, maxCounts })

    setHeader(ws)
    settingCells(ws)

    setColumnWidth({ ws, marksHeader })

    onProgress(++progressIndex)
  }

  await onDownloadFile({ wb, classData, semester })
}

const getSubjects = async (slots = [], titleNePostfix = '') => {
  const subjectIds = uniq(slots.map(x => x.subject.id))
  const subjects = await Subject.fetch({
    id_in: subjectIds,
    _populate: 'subjectGroup',
    _sort: 'subjectGroupIndex:ASC'
  })

  return subjects
}

const exec = async ({ semester = {}, classData = {}, currentSchoolYear = {} }) => {
  let [slots, rawMarks, students] = await Promise.all([
    Slot.fetch({ grade: classData.grade.id, _populate: 'subject' }),
    Mark.fetch({
      semester: semester.id,
      class: classData.id,
      _limit: -1,
      _populate: 'factor'
    }),
    Student.fetch({ currentClass: classData.id, _populate: 'academicLevel' })
  ])
  const subjects = await getSubjects(slots, classData.grade.gradeNumber)
  const collator = new Intl.Collator('vi')
  students = students
    .map(s => {
      const fn = s.formatedName.split(' ').shift()
      const ln = s.formatedName.replace(`${fn} `, '')
      return { ...s, fn, ln }
    })
    .sort((a, b) => {
      const r = collator.compare(a.fn, b.fn)
      return r !== 0 ? r : collator.compare(a.ln, b.ln)
    })

  await generateFile({
    rawMarks,
    semester,
    students,
    classData,
    currentSchoolYear,
    subjects
  })
}

export default { exec }
